import { fg } from '@atlaskit/platform-feature-flags';

import type {
	SelectionType,
	UpdateIdMap,
	EditorPluginAIConfigItem,
} from '../config-items/config-items';
import type { MentionMap } from '../utils/mentions/get-mention-map';

import { getRequestOptions4ConvoAI } from './request-options-convo-ai';
import { getRequestOptions4RovoChat } from './request-options-rovo-agent';
import { getRequestOptions4XPGenAI, shouldUseXPGenAI } from './request-options-xp-gen-ai';
import type { AIExperienceMachineContextForRequest, RequestOptions, ResponseObject } from './types';

export function getRequestOptions({
	callback,
	context,
	configItem,
	isEmpty,
	selectionType,
	mentionMap,
}: {
	callback: (event: ResponseObject) => void;
	context: AIExperienceMachineContextForRequest;
	configItem: EditorPluginAIConfigItem;
	// remove this when cleanup platform_editor_ai_remove_selection_type
	isEmpty?: boolean;
	// remove this when cleanup platform_editor_ai_remove_selection_type
	selectionType?: SelectionType;
	mentionMap?: MentionMap;
}): RequestOptions {
	// elevate free generate is disabled and returns the pre-configured response immediately
	if (configItem.response) {
		return { response: configItem.response };
	}

	const { latestPromptResponse } = context;
	const intentSchemaId =
		typeof configItem.intentSchemaId === 'function'
			? configItem.intentSchemaId({ latestPromptResponse })
			: configItem.intentSchemaId;
	const updateIdMap: UpdateIdMap = ({ idMap }) => {
		callback({
			type: 'update idMap',
			context: {
				idMap,
				selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
			},
		});
	};

	let requestOptions: RequestOptions;

	if (shouldUseXPGenAI({ configItem, latestPromptResponse })) {
		requestOptions = getRequestOptions4XPGenAI({
			context,
			configItem,
			mentionMap,
			isEmpty: fg('platform_editor_ai_remove_selection_type') ? undefined : isEmpty,
			selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
			intentSchemaId,
			updateIdMap,
		});
	} else if (configItem.agent) {
		requestOptions = getRequestOptions4RovoChat({
			context,
			configItem,
			mentionMap,
			selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
			updateIdMap,
		});
	} else {
		requestOptions = getRequestOptions4ConvoAI({
			context,
			configItem,
			mentionMap,
			isEmpty: fg('platform_editor_ai_remove_selection_type') ? undefined : isEmpty,
			selectionType: fg('platform_editor_ai_remove_selection_type') ? undefined : selectionType,
			intentSchemaId,
			updateIdMap,
		});
	}

	if ('contentStatistics' in requestOptions && requestOptions.contentStatistics) {
		callback({
			type: 'storeContextStatistics',
			contextStatistics: { inputDocument: requestOptions.contentStatistics },
		});
	}

	return requestOptions;
}
